<template lang="pug">
.author-audio-jit.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700.text-gray-900.dark_text-gray-100
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.dark_text-gray-100.group.rounded-md.p-2.text-sm.hover_bg-primary-100(href="#/home")
          | Home - all courses
    .ms-auto
    ToggleDark

  .flex-1.p-8.overflow-y-auto.overflow-x-hidden
    .max-w-screen-2xl.mx-auto
      .prose.dark_prose-invert.max-w-none
        h1.font-normal Audio AI JiT
        p.opacity-80
          | Load a course to enumerate and (re)generate its JiT audio slots. Course loading, base+custom merge and translations are handled by the real runtime — this page just drives it.

      .grid.grid-cols-1.xl_grid-cols-3.gap-6.mt-6
        //- ── Left column: TTS settings ──────────────────────────────────────
        .xl_col-span-1.p-4.bg-gray-100.dark_bg-gray-800.rounded-lg.text-gray-900.dark_text-gray-100
          h2.mt-0.mb-2.text-xl.text-gray-900.dark_text-gray-100 TTS settings

          //- Course mode: read-only config reported by the runtime (from YAML).
          template(v-if="courseLoaded")
            p.mt-0.text-xs.opacity-60.mb-3 Resolved from the course's audio-ai-jit config.
            dl.text-sm.grid.grid-cols-3.gap-x-3.gap-y-2(v-if="jitEnabled")
              dt.opacity-60 Provider
              dd.col-span-2.font-medium {{ providerId }}
              dt.opacity-60 Voice
              dd.col-span-2.font-medium.break-all {{ voiceOverride || '—' }}
              dt.opacity-60 Voice locale
              dd.col-span-2.font-medium {{ voiceLocaleOverride || '—' }}
              dt.opacity-60 Course locale
              dd.col-span-2.font-medium {{ courseLocale || '—' }}
            p.text-sm.opacity-70(v-else) Audio AI JiT is not enabled for this course/language.

          //- Standalone sandbox: editable settings + voice sampling/preview.
          template(v-else)
            p.mt-0.text-sm.text-red-600.dark_text-red-400(v-if="resolveError") {{ resolveError }}

            .space-y-3
              label.block
                span.block.text-sm.font-medium.mb-1 Provider
                select.w-full.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="providerId"
                )
                  option(v-for="provider in providers" :key="provider.id" :value="provider.id")
                    | {{ provider.label }}

              label.block
                span.block.text-sm.font-medium.mb-1 Model
                input.w-full.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="modelOverride"
                  :placeholder="currentProvider.defaultModels[0] || 'Model id'"
                  disabled
                )

              label.block
                span.block.text-sm.font-medium.mb-1 Course locale
                input.w-full.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="courseLocale"
                  placeholder="e.g. en-GB"
                )

              label.block
                span.block.text-sm.font-medium.mb-1 Voice locale override
                input.w-full.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="voiceLocaleOverride"
                  :placeholder="resolvedDefaults?.voiceLocale || 'Optional BCP-47 override'"
                )
                p.mt-1.text-xs.opacity-70(v-if="resolvedDefaults") Resolved: {{ resolvedDefaults.voiceLocale }}

              label.block
                span.block.text-sm.font-medium.mb-1 Voice
                .flex.gap-2
                  input.flex-1.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                    v-model="voiceOverride"
                    :placeholder="resolvedDefaults?.voice || 'Voice name/id'"
                  )
                  button.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-3.rounded(
                    type="button"
                    :disabled="loadingVoices"
                    @click="loadVoices"
                  ) {{ loadingVoices ? '...' : 'Load voices' }}
                p.mt-1.text-xs.text-red-600.dark_text-red-400(v-if="voiceLoadError") {{ voiceLoadError }}

              .max-h-32.overflow-y-auto.border.rounded-md.p-2.bg-white.dark_bg-gray-900(
                v-if="voiceOptions.length > 0"
              )
                button.block.w-full.text-left.py-1.px-2.rounded.hover_bg-gray-100.dark_hover_bg-gray-800(
                  v-for="voice in voiceOptions"
                  :key="voice.id"
                  type="button"
                  @click="voiceOverride = voice.id"
                )
                  span.font-medium {{ voice.name }}
                  span.opacity-70(v-if="voice.locale")  ({{ voice.locale }})

            h3.mt-6.mb-2.text-lg.text-gray-900.dark_text-gray-100 Voice preview
            textarea.w-full.px-3.py-2.border.rounded-md.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
              rows="4"
              v-model="previewText"
              placeholder="Type text and click preview to test current voice settings."
            )
            .flex.items-center.gap-2.mt-2
              button.btn-hover_cursor-pointer.bg-yellow-500.btn-hover_bg-yellow-600.disabled_bg-gray-600.disabled_opacity-80.group.text-gray-900.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                :disabled="previewPending || !previewText.trim()"
                @click="playPreview"
              ) {{ previewPending ? 'Generating...' : 'Preview voice' }}
              span.text-sm.opacity-75(v-if="previewMetrics")
                | {{ previewMetrics.source }} - {{ previewMetrics.latencyMs.toFixed(0) }}ms - ${{ previewMetrics.costUsd.toFixed(5) }}
            audio.w-full.mt-2(v-if="previewAudioSrc" :src="previewAudioSrc" controls)

          //- Cache settings — apply to both the voice preview and course warm/regen.
          h3.mt-6.mb-2.text-lg.text-gray-900.dark_text-gray-100 Cache settings
          .space-y-2.flex.flex-col.gap-2
            label.inline-flex.items-center.gap-2
              input(type="checkbox" v-model="cacheSettings.allowRead")
              span Allow read
            label.inline-flex.items-center.gap-2
              input(type="checkbox" v-model="cacheSettings.allowWrite")
              span Allow write
          p.mt-2.text-xs.opacity-60 Uncheck #[b Allow read] to force regeneration (overwrite cached clips).

        //- ── Right column: Course loader ─────────────────────────────
        .xl_col-span-2.p-4.bg-gray-100.dark_bg-gray-800.rounded-lg.text-gray-900.dark_text-gray-100
          h2.mt-0.mb-2.text-xl.text-gray-900.dark_text-gray-100 Course audio

          .p-3.bg-blue-50.dark_bg-blue-950.rounded-lg.border.border-blue-200.dark_border-blue-800
            .flex.gap-2.items-end.flex-wrap
              label.block.flex-1(style="min-width: 8rem")
                span.block.text-xs.font-medium.mb-1 Course ID
                input.w-full.px-2.py-1.border.rounded.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="courseIdInput"
                  type="number"
                  placeholder="e.g. 1234"
                )
              label.block.flex-1(style="min-width: 8rem")
                span.block.text-xs.font-medium.mb-1 Firm code (optional)
                input.w-full.px-2.py-1.border.rounded.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="firmCodeInput"
                  placeholder="e.g. mycompany"
                )
              button.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.text-white.text-center.font-bold.py-1.px-4.rounded.whitespace-nowrap(
                type="button"
                :disabled="courseLoading || !courseIdInput"
                @click="loadCourseSlots()"
              ) {{ courseLoading ? 'Loading…' : 'Load course' }}
              button.btn-hover_cursor-pointer.bg-gray-200.dark_bg-gray-700.btn-hover_bg-gray-300.dark_btn-hover_bg-gray-600.disabled_bg-gray-600.disabled_opacity-80.text-gray-900.dark_text-gray-100.text-center.font-bold.py-1.px-4.rounded.border.border-gray-300.dark_border-gray-600.whitespace-nowrap(
                v-if="courseLoaded"
                type="button"
                @click="resetCourse"
              ) Reset
            p.mt-2.text-xs.text-red-600.dark_text-red-400(v-if="courseLoadError") {{ courseLoadError }}
            p.mt-2.text-xs.opacity-60 Or add to the URL as #[code #/audio-jit/firm_courseId]

            //- Language selector — shown after a course is attached
            .mt-3.flex.items-center.gap-3(v-if="courseLoaded && availableLanguages.length > 0")
              label.block.flex-1
                span.block.text-xs.font-medium.mb-1 Language
                select.w-full.px-2.py-1.border.rounded.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                  v-model="selectedLanguage"
                  :disabled="courseLoading"
                  @change="onLanguageChange"
                )
                  option(v-for="lang in availableLanguages" :key="lang" :value="lang") {{ lang }}

          //- JIT not enabled for this course/language
          .mt-4.p-3.rounded-lg.bg-yellow-50.dark_bg-yellow-950.border.border-yellow-200.dark_border-yellow-800.text-md(
            v-if="courseLoaded && !jitEnabled"
          )
            | Audio AI JiT is not enabled for this course/language. Nothing to generate.

          .mt-4(v-if="courseLoaded && jitEnabled && screenGroups.length > 0")
            .flex.items-center.justify-between.flex-wrap.gap-2.mb-3
              .flex.items-center.gap-3
                span.text-base.font-medium.text-gray-900.dark_text-gray-100
                  | {{ screenGroups.length }} screens · {{ allSlots.length }} slots
                span.text-sm.opacity-70 ${{ totalEstimatedCost.toFixed(5) }}
              button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.text-white.text-center.font-bold.py-1.px-4.rounded.text-sm(
                type="button"
                :disabled="!generateAllPending && allSlots.filter(s => s.text.trim()).length === 0"
                @click="generateAllPending ? cancelGenerateAll() : generateAll()"
              )
                | {{ generateAllPending ? `Cancel (${generateAllProgress.done}/${generateAllProgress.total}${generateAllProgress.errors ? ', ' + generateAllProgress.errors + ' errors' : ''})` : (forceMode ? 'Force regenerate all' : 'Generate all') }}

            .overflow-y-auto.pr-1(class="max-h-[65vh]")
              .mb-4(v-for="group in screenGroups" :key="group.screenId")
                .px-3.py-2.bg-gray-200.dark_bg-gray-700.rounded-t-lg.flex.items-center.gap-2
                  span.text-sm.font-mono.opacity-60 {{ group.screenId }}
                  span.text-base.font-semibold.truncate(v-if="group.screenLabel !== group.screenId") {{ group.screenLabel }}
                  span.ml-auto.text-xs.opacity-50 {{ group.slots.length }} {{ group.slots.length === 1 ? 'slot' : 'slots' }}

                .border.border-gray-200.dark_border-gray-700.rounded-b-lg.divide-y.divide-gray-200.dark_divide-gray-700
                  .p-3.bg-white.dark_bg-gray-900.text-gray-900.dark_text-gray-100(
                    v-for="slot in group.slots"
                    :key="slot.uid"
                  )
                    .flex.items-center.gap-2.flex-wrap
                      span.text-sm.font-mono.opacity-60.truncate {{ slot.key }}
                      span.text-sm.opacity-50.ml-auto {{ slot.text.length }} chars
                    textarea.w-full.mt-2.px-3.py-2.border.rounded-md.bg-gray-50.dark_bg-gray-800.text-gray-900.dark_text-gray-100.font-monospace.text-sm(
                      :rows="slotTextRows(slot.text)"
                      v-model="slot.text"
                      disabled
                    )
                    .flex.items-center.gap-2.mt-2.flex-wrap
                      button.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-1.px-3.rounded.text-sm(
                        type="button"
                        :disabled="slot.status === 'pending' || !slot.text.trim()"
                        @click="playSlot(slot)"
                      ) {{ slot.status === 'pending' ? 'Generating…' : (forceMode ? 'Regenerate' : 'Play') }}
                      span.text-sm(v-if="slot.status === 'error'").text-red-600.dark_text-red-400 {{ slot.error || 'Failed' }}
                      span.text-sm.opacity-80(v-else-if="slot.source")
                        | {{ slot.source }}{{ slot.costUsd != null ? ` · $${slot.costUsd.toFixed(5)}` : '' }}
                    audio.w-full.mt-2(
                      v-if="slot.audioSrc"
                      :src="slot.audioSrc"
                      controls
                      :ref="el => setAudioRef(slot.uid, el)"
                    )

  //- Hidden runtime engine — loads the real ubercourse player to do authentic
  //- course loading / translations / JiT enumeration via window.ucJit.
  iframe(
    ref="runtimeIframe"
    referrerpolicy="origin-when-cross-origin"
    style="position:absolute; width:0; height:0; border:0; visibility:hidden;"
  )
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import {
  getAudioJitCacheBaseUrl,
  getProvider,
  providerList,
  resolveJitConfig,
  runJITSynthesis,
  type CacheSettings,
  type JitConfigOverrides,
  type JitResolvedConfig,
  type JITResult,
  type TTSProviderId,
  type VoiceOption
} from '../../lib/audioJit';
import type { UcJitApi } from '../../lib/useJitAuthorApi';
import { loadCourse } from '../store/courses';

const props = defineProps<{
  courseId?: number;
  firmCode?: string;
}>();

// ---------------------------------------------------------------------------
// Slot / group types
// ---------------------------------------------------------------------------

interface SlotRow {
  /** Unique per-row identity (cache marker `key` is not unique — e.g. course title+subtitle share it). */
  uid: string;
  key: string;
  text: string;
  status: 'idle' | 'pending' | 'error' | 'success';
  error?: string;
  audioSrc?: string;
  source?: string;
  costUsd?: number;
}

interface GroupRow {
  screenId: string;
  screenLabel: string;
  slots: SlotRow[];
}

// ---------------------------------------------------------------------------
// TTS settings + standalone voice preview (POC sandbox)
// ---------------------------------------------------------------------------

const providers = providerList;
const providerId = ref<TTSProviderId>('google');
const loadingVoices = ref(false);
const voiceLoadError = ref('');
const voiceOptions = ref<VoiceOption[]>([]);
const objectUrls = new Set<string>();

const previewText = ref(
  'This is a quick voice preview from Author Audio AI JiT.'
);
const previewPending = ref(false);
const previewAudioSrc = ref('');
const previewMetrics = ref<JITResult | null>(null);

const courseLocale = ref('en-GB');
const modelOverride = ref('');
const voiceOverride = ref('');
const voiceLocaleOverride = ref('');
const resolveError = ref('');

const cacheSettings = reactive<CacheSettings>({
  allowLocalRuntime: false,
  allowWrite: true,
  allowRead: true
});

const currentProvider = computed(() => getProvider(providerId.value));

const jitOverrides = computed(
  (): JitConfigOverrides => ({
    provider: providerId.value,
    locale: courseLocale.value,
    ...(modelOverride.value.trim()
      ? { model: modelOverride.value.trim() }
      : {}),
    ...(voiceOverride.value.trim()
      ? { voice: voiceOverride.value.trim() }
      : {}),
    ...(voiceLocaleOverride.value.trim()
      ? { voiceLocale: voiceLocaleOverride.value.trim() }
      : {})
  })
);

const resolvedConfig = computed((): JitResolvedConfig | null => {
  resolveError.value = '';
  try {
    return resolveJitConfig(jitOverrides.value);
  } catch (error: any) {
    resolveError.value = error.message || 'Invalid language/provider settings.';
    return null;
  }
});

const resolvedDefaults = computed(() => {
  try {
    return resolveJitConfig({
      provider: providerId.value,
      locale: courseLocale.value
    });
  } catch {
    return null;
  }
});

const safeRevokeObjectUrl = (url?: string) => {
  if (!url || !url.startsWith('blob:')) return;
  URL.revokeObjectURL(url);
  objectUrls.delete(url);
};

const loadVoices = async () => {
  loadingVoices.value = true;
  voiceLoadError.value = '';
  if (!resolvedConfig.value) {
    voiceLoadError.value =
      resolveError.value || 'Resolve settings before loading voices.';
    loadingVoices.value = false;
    return;
  }
  try {
    voiceOptions.value = await currentProvider.value.fetchVoices(
      resolvedConfig.value
    );
  } catch (error: any) {
    voiceLoadError.value =
      error.message ||
      'Voice list unavailable for this provider/configuration.';
    voiceOptions.value = [];
  } finally {
    loadingVoices.value = false;
  }
};

const playPreview = async () => {
  previewPending.value = true;
  try {
    if (!resolvedConfig.value)
      throw new Error(resolveError.value || 'Invalid TTS settings.');
    const result = await runJITSynthesis({
      text: previewText.value,
      providerConfig: resolvedConfig.value,
      cacheSettings,
      cacheBaseUrl: getAudioJitCacheBaseUrl()
    });
    safeRevokeObjectUrl(previewAudioSrc.value);
    previewAudioSrc.value = result.audioSrc;
    if (previewAudioSrc.value.startsWith('blob:'))
      objectUrls.add(previewAudioSrc.value);
    previewMetrics.value = result;
    await nextTick();
  } catch (error: any) {
    previewMetrics.value = null;
    console.error(error);
  } finally {
    previewPending.value = false;
  }
};

// ---------------------------------------------------------------------------
// Course mode — drive the real runtime via a hidden iframe (window.ucJit)
// ---------------------------------------------------------------------------

const runtimeIframe = ref<HTMLIFrameElement | null>(null);
const ucJit = ref<UcJitApi | null>(null);
const courseIdInput = ref('');
const firmCodeInput = ref('');
const courseLoading = ref(false);
const courseLoadError = ref('');
const jitEnabled = ref(false);
const availableLanguages = ref<string[]>([]);
const selectedLanguage = ref('en-GB');
/** Force-regenerate mode = cache reads disabled, so synthesis always overwrites. */
const forceMode = computed(() => !cacheSettings.allowRead);
const screenGroups = ref<GroupRow[]>([]);
const audioRefs = ref<Record<string, HTMLAudioElement | null>>({});

let loadSeq = 0;

const courseLoaded = computed(() => ucJit.value !== null);

const allSlots = computed<SlotRow[]>(() =>
  screenGroups.value.flatMap(g => g.slots)
);

const totalEstimatedCost = computed(() =>
  allSlots.value.reduce(
    (sum, s) => (s.source === 'provider' && s.costUsd ? sum + s.costUsd : sum),
    0
  )
);

const slotTextRows = (text: string) =>
  Math.min(Math.max((text ?? '').split('\n').length, 1), 5);

const setAudioRef = (key: string, element: any) => {
  audioRefs.value[key] = element as HTMLAudioElement | null;
};

const buildRuntimeUrl = (lang?: string): string => {
  const courseId = Number(courseIdInput.value);
  const firm = firmCodeInput.value.trim();
  // No `lang` on the first load: let the runtime resolve its own default-language
  // so audio-ai-jit (often keyed per-locale) evaluates as enabled. Forcing an
  // arbitrary locale (e.g. en-GB) here is what flipped `enabled` true→false.
  const queryString = [
    'turbo=999999',
    `courseid=${courseId}`,
    firm ? `firm=${firm}` : '',
    lang ? `lang=${lang}` : '',
    'tags=worddoc+includeall&audioDoc=1&ucAuthorAudio=1'
  ]
    .filter(Boolean)
    .join('&');

  const localServer = window.location.origin.includes('local');
  const portalOrigin = new URL(window.location.href).origin;
  return localServer
    ? `./app.html?${queryString}`
    : `${portalOrigin}/latest/app.html?${queryString}`;
};

/** Load the runtime into the iframe and resolve once `window.ucJit` is ready. */
const loadRuntimeBridge = (src: string, seq: number): Promise<UcJitApi> =>
  new Promise<UcJitApi>((resolve, reject) => {
    const iframe = runtimeIframe.value;
    if (!iframe) {
      reject(new Error('Runtime iframe not available.'));
      return;
    }

    let settled = false;
    const settle = (fn: () => void) => {
      if (!settled && seq === loadSeq) {
        settled = true;
        fn();
      }
    };

    const poll = () => {
      if (settled || seq !== loadSeq) return;
      const cw = iframe.contentWindow as (Window & { ucJit?: UcJitApi }) | null;
      if (cw?.ucJit?.ready) {
        settle(() => resolve(cw.ucJit as UcJitApi));
        return;
      }
      window.setTimeout(poll, 300);
    };

    iframe.addEventListener(
      'load',
      () => {
        const cw = iframe.contentWindow as Window;
        cw.onerror = message =>
          settle(() => reject(new Error(`Runtime error: ${String(message)}`)));
        poll();
      },
      { once: true }
    );

    window.setTimeout(
      () =>
        settle(() => reject(new Error('Timed out loading course runtime.'))),
      60000
    );

    iframe.src = src;
  });

const populateFromBridge = (jit: UcJitApi) => {
  ucJit.value = jit;
  jitEnabled.value = jit.enabled;

  if (!jit.enabled) {
    screenGroups.value = [];
    return;
  }

  // Lock the TTS panel to the course's config (display + voice sampling).
  if (jit.provider) providerId.value = jit.provider as TTSProviderId;
  voiceOverride.value = jit.voice ?? '';
  voiceLocaleOverride.value = jit.voiceLocale ?? '';
  courseLocale.value = jit.locale ?? courseLocale.value;

  // Reset the element-ref map: cache marker keys are not unique per row, so
  // refs are keyed by a positional uid (see SlotRow.uid).
  audioRefs.value = {};
  screenGroups.value = jit.list().map((g, gi) => ({
    screenId: g.screenId,
    screenLabel: g.screenLabel,
    slots: g.slots.map((s, si) => ({
      uid: `${gi}:${si}`,
      key: s.key,
      text: s.text,
      status: 'idle' as const
    }))
  }));
};

/**
 * Boot the runtime iframe and attach to its `window.ucJit`. On the first call
 * (`lang` omitted) the runtime resolves its own default locale; if that locale
 * isn't JiT-enabled we reload once at the first JiT-enabled locale so `enabled`
 * and the translations line up.
 */
const attachRuntime = async (lang?: string): Promise<void> => {
  courseLoading.value = true;
  courseLoadError.value = '';
  allSlots.value.forEach(s => safeRevokeObjectUrl(s.audioSrc));

  const seq = ++loadSeq;
  try {
    const jit = await loadRuntimeBridge(buildRuntimeUrl(lang), seq);
    if (seq !== loadSeq) return;

    const enabledLangs = jit.jitLanguages;
    availableLanguages.value = enabledLangs;

    if (!enabledLangs.length) {
      // Course has no JiT-enabled locale at all.
      ucJit.value = jit;
      jitEnabled.value = false;
      screenGroups.value = [];
      return;
    }

    // Resolve to a JiT-enabled locale: honour an explicit choice, else the
    // runtime's current locale if enabled, else the first enabled locale.
    const desired =
      lang && enabledLangs.includes(lang)
        ? lang
        : enabledLangs.includes(jit.locale)
          ? jit.locale
          : enabledLangs[0];

    if (desired !== jit.locale) {
      // Runtime booted on a non-JiT locale — reload once at the correct one.
      selectedLanguage.value = desired;
      await attachRuntime(desired);
      return;
    }

    selectedLanguage.value = desired;
    populateFromBridge(jit);
  } catch (error: any) {
    if (seq !== loadSeq) return;
    courseLoadError.value = error?.message || 'Failed to load course runtime.';
    ucJit.value = null;
    jitEnabled.value = false;
    screenGroups.value = [];
  } finally {
    if (seq === loadSeq) courseLoading.value = false;
  }
};

const loadCourseSlots = async () => {
  if (!courseIdInput.value || !runtimeIframe.value) return;

  courseLoading.value = true;
  courseLoadError.value = '';

  // Unsaved-changes guard (mirrors AuthorAudio): only generate audio from
  // committed content, since the runtime iframe loads the latest saved/draft.
  try {
    const course = await loadCourse({
      courseId: Number(courseIdInput.value),
      firmCode: firmCodeInput.value.trim() || undefined
    });
    if (!course?.latest?.courseId) {
      courseLoadError.value = 'Course not found.';
      courseLoading.value = false;
      return;
    }
    if (course.hasUnsavedChanges) {
      const who = course.latestDraft?.author;
      courseLoadError.value = `Course has unsaved changes${
        who ? ` from ${who}` : ''
      }. Please save/commit before generating audio.`;
      courseLoading.value = false;
      return;
    }
  } catch (error: any) {
    courseLoadError.value = error?.message || 'Failed to load course.';
    courseLoading.value = false;
    return;
  }

  // First load: no explicit lang — let the runtime pick its default locale.
  await attachRuntime();
};

const onLanguageChange = () => {
  void attachRuntime(selectedLanguage.value);
};

const resetCourse = () => {
  loadSeq++;
  allSlots.value.forEach(s => safeRevokeObjectUrl(s.audioSrc));
  screenGroups.value = [];
  ucJit.value = null;
  jitEnabled.value = false;
  availableLanguages.value = [];
  selectedLanguage.value = 'en-GB';
  courseLoadError.value = '';
  if (runtimeIframe.value) runtimeIframe.value.src = 'about:blank';
};

// ---------------------------------------------------------------------------
// Synthesis (per-slot + generate all) — all via the runtime bridge
// ---------------------------------------------------------------------------

const playSlot = async (slot: SlotRow) => {
  const jit = ucJit.value;
  if (!jit) return;

  slot.status = 'pending';
  slot.error = '';
  try {
    const result = await jit.warm(slot.text, {
      allowRead: cacheSettings.allowRead,
      allowWrite: cacheSettings.allowWrite
    });
    slot.source = result.source;
    slot.costUsd = result.costUsd;
    slot.audioSrc = result.cacheUrl;
    slot.status = 'success';
    await nextTick();
    await audioRefs.value[slot.uid]?.play();
  } catch (error: any) {
    slot.status = 'error';
    slot.error = error?.message || 'TTS request failed.';
  }
};

const generateAllPending = ref(false);
const generateAllProgress = reactive({ done: 0, total: 0, errors: 0 });
let generateAllAbort: AbortController | null = null;

const generateAll = async () => {
  const jit = ucJit.value;
  if (!jit) return;
  const eligible = allSlots.value.filter(s => s.text.trim());
  if (!eligible.length) return;

  generateAllPending.value = true;
  generateAllProgress.done = 0;
  generateAllProgress.total = eligible.length;
  generateAllProgress.errors = 0;

  generateAllAbort = new AbortController();
  const signal = generateAllAbort.signal;

  try {
    for (const slot of eligible) {
      if (signal.aborted) break;
      slot.status = 'pending';
      slot.error = '';
      try {
        const result = await jit.warm(slot.text, {
          allowRead: cacheSettings.allowRead,
          allowWrite: cacheSettings.allowWrite
        });
        slot.source = result.source;
        slot.costUsd = result.costUsd;
        slot.audioSrc = result.cacheUrl;
        slot.status = 'success';
      } catch (error: any) {
        slot.status = 'error';
        slot.error = error?.message || 'Failed';
        generateAllProgress.errors++;
      }
      generateAllProgress.done++;
    }
  } finally {
    generateAllPending.value = false;
    generateAllAbort = null;
  }
};

const cancelGenerateAll = () => {
  generateAllAbort?.abort();
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
  if (props.courseId) {
    courseIdInput.value = String(props.courseId);
    firmCodeInput.value = props.firmCode ?? '';
    void loadCourseSlots();
  }
});

onUnmounted(() => {
  loadSeq++;
  allSlots.value.forEach(s => safeRevokeObjectUrl(s.audioSrc));
  safeRevokeObjectUrl(previewAudioSrc.value);
  objectUrls.forEach(url => URL.revokeObjectURL(url));
  objectUrls.clear();
});

useDocTitle(computed(() => 'Audio AI JiT'));
</script>

<style scoped></style>
